import type { Command, GameState, ResolutionEvent } from "../domain/types/index.js";
import { getCard, getCase } from "../content/lookups.js";
import {
  playActionCardUse,
  playBadEvent,
  playBossClear,
  playBreakthroughPraise,
  playCardDraw,
  playCardUse,
  playCaseFailure,
  playCaseSuccess,
  playDeadlineWarning,
  playFatigueGain,
  playFatigueHeal,
  playGoodEvent,
  playGameOver,
  playProgressPraise,
  playReward,
  playStageStart,
  playUiBlocked,
  playUiConfirm,
  playUiSelect,
} from "./soundLab.js";
import { playClearCheer } from "./clearCheerPlayer.js";

/**
 * The game only ever refers to these semantic IDs. To replace a generated sound
 * with an MP3 later, change the matching branch here; no gameplay component
 * needs to know about an audio filename.
 */
export type GameSound =
  | "ui.select"
  | "ui.confirm"
  | "ui.blocked"
  | "stage.start"
  | "card.draw"
  | "card.use"
  | "card.action-use"
  | "progress.small"
  | "progress.large"
  | "progress.breakthrough"
  | "fatigue.heal"
  | "fatigue.gain"
  | "deadline.warning"
  | "event.good"
  | "event.bad"
  | "reward"
  | "case.success"
  | "case.failure"
  | "boss.clear"
  | "gameover";

export type SoundCatalogItem = {
  sound: GameSound;
  name: string;
  description: string;
  actionUseCount?: 1 | 2 | 3 | 4 | 5;
};

/** The hidden sound catalog reads from this list so every player-facing cue is auditionable. */
export const SOUND_CATALOG: readonly SoundCatalogItem[] = [
  { sound: "ui.select", name: "選択", description: "メニューや選択肢に触れたとき" },
  { sound: "ui.confirm", name: "決定", description: "選択を確定したとき" },
  { sound: "ui.blocked", name: "操作不可", description: "実行できない操作をしたとき" },
  { sound: "stage.start", name: "ステージ開始", description: "新しいランを始めたとき" },
  { sound: "card.draw", name: "カードを引く", description: "プロジェクト開始時の手札配布" },
  { sound: "card.use", name: "カード使用", description: "ワーカーを配置したとき" },
  { sound: "card.action-use", name: "アクション使用", description: "アクションカードを使用したとき（3枚目）", actionUseCount: 3 },
  { sound: "progress.small", name: "小さな進捗", description: "通常の進捗獲得" },
  { sound: "progress.large", name: "大きな進捗", description: "大きな進捗獲得" },
  { sound: "progress.breakthrough", name: "ブレイクスルー", description: "10以上の進捗獲得" },
  { sound: "fatigue.heal", name: "疲労回復", description: "休憩や回復報酬" },
  { sound: "fatigue.gain", name: "疲労増加", description: "トラブルで疲労を受けたとき" },
  { sound: "deadline.warning", name: "期限警告", description: "期限が迫ったとき" },
  { sound: "event.good", name: "良いイベント", description: "有利なイベント効果" },
  { sound: "event.bad", name: "悪いイベント", description: "不利なイベント効果" },
  { sound: "reward", name: "報酬", description: "カード・組織基盤を獲得したとき" },
  { sound: "case.success", name: "PJ成功", description: "通常プロジェクトを解決したとき" },
  { sound: "case.failure", name: "PJ失敗", description: "通常プロジェクトに失敗したとき" },
  { sound: "boss.clear", name: "ボスPJクリア", description: "最終プロジェクトを解決したとき" },
  { sound: "gameover", name: "ゲームオーバー", description: "ランが終了したとき" },
];

function supported(): boolean {
  return typeof AudioContext !== "undefined";
}

export function playGameSound(sound: GameSound, actionUseCount: 1 | 2 | 3 | 4 | 5 = 1): void {
  if (!supported()) return;
  switch (sound) {
    case "ui.select": void playUiSelect(); break;
    case "ui.confirm": void playUiConfirm(); break;
    case "ui.blocked": void playUiBlocked(); break;
    case "stage.start": void playStageStart(); break;
    case "card.draw": void playCardDraw(); break;
    case "card.use": void playCardUse(); break;
    case "card.action-use": void playActionCardUse(actionUseCount); break;
    case "progress.small": void playProgressPraise(2); break;
    case "progress.large": void playProgressPraise(4); break;
    case "progress.breakthrough": void playBreakthroughPraise(); break;
    case "fatigue.heal": void playFatigueHeal(); break;
    case "fatigue.gain": void playFatigueGain(); break;
    case "deadline.warning": void playDeadlineWarning(); break;
    case "event.good": void playGoodEvent(); break;
    case "event.bad": void playBadEvent(); break;
    case "reward": void playReward(); break;
    case "case.success":
      void playCaseSuccess();
      playClearCheer();
      break;
    case "case.failure": void playCaseFailure(); break;
    case "boss.clear":
      void playBossClear();
      playClearCheer();
      break;
    case "gameover": void playGameOver(); break;
  }
}

function resolutionSound(event: ResolutionEvent, isBoss: boolean): GameSound | null {
  switch (event.kind) {
    case "progress":
      if (event.gain >= 10) return "progress.breakthrough";
      return event.gain >= 5 ? "progress.large" : "progress.small";
    case "heal": return "fatigue.heal";
    case "load": return event.fatigueDealt > 0 ? "fatigue.gain" : null;
    case "deadline":
    case "overdue": return "deadline.warning";
    case "success": return isBoss ? "boss.clear" : "case.success";
    case "failure": return "case.failure";
    case "gameover": return "gameover";
    default: return null;
  }
}

function eventChoiceSound(state: GameState): GameSound {
  const effects = state.activeEvent?.effects ?? [];
  if (effects.some((effect) => effect.kind === "fatigue" && effect.amount < 0)) return "fatigue.heal";
  if (effects.some((effect) => effect.kind === "fatigue" && effect.amount > 0)) return "fatigue.gain";
  if (effects.some((effect) => effect.kind === "nextCase" && effect.stat === "deadline" && effect.amount > 0)) return "event.good";
  return "reward";
}

/** Plays one cue after a successful reducer transition. */
export function playSoundForTransition(previous: GameState, next: GameState, command: Command): void {
  if (!supported()) return;
  if (next.lastRejection && next.lastRejection !== previous.lastRejection) {
    playGameSound("ui.blocked");
    return;
  }

  const resolution = next.resolution;
  const enteredResolution = next.screen === "case.resolve"
    && resolution !== null
    && (previous.screen !== "case.resolve" || previous.resolution?.index !== resolution.index);
  if (enteredResolution) {
    const isBoss = getCase(next.run?.activeCase?.caseId ?? "")?.isBoss === true;
    const sound = resolutionSound(resolution.events[resolution.index], isBoss);
    if (sound) playGameSound(sound);
    return;
  }

  switch (command.type) {
    case "OPEN_STAGE_SELECT":
    case "OPEN_BRING_IN":
    case "SELECT_NODE":
      playGameSound("ui.confirm");
      if (next.screen === "case.play" && previous.screen === "map") window.setTimeout(() => playGameSound("card.draw"), 90);
      return;
    case "BACK_TO_TITLE":
    case "TOGGLE_SELECTION":
      playGameSound("ui.select");
      return;
    case "START_NEW":
      if (next.screen === "map") playGameSound("stage.start");
      return;
    case "PLAY_CARD": {
      const card = previous.run?.hand.find((candidate) => candidate.instanceId === command.instanceId);
      const isAction = getCard(card?.cardId ?? "")?.type === "action";
      const sound: GameSound = isAction
        ? "card.action-use"
        : "card.use";
      const actionUseCount = Math.min(
        5,
        next.run?.inPlay.filter((candidate) => getCard(candidate.cardId)?.type === "action").length ?? 1,
      ) as 1 | 2 | 3 | 4 | 5;
      playGameSound(sound, actionUseCount);
      if (isAction) return;
      const beforeProgress = previous.run?.activeCase?.progress ?? 0;
      const afterProgress = next.run?.activeCase?.progress ?? 0;
      if (afterProgress > beforeProgress) {
        const gain = afterProgress - beforeProgress;
        const progressSound: GameSound = gain >= 10
          ? "progress.breakthrough"
          : gain >= 5
            ? "progress.large"
            : "progress.small";
        window.setTimeout(() => playGameSound(progressSound), 110);
      }
      return;
    }
    case "SUBMIT_SELECTION":
      playGameSound("ui.confirm");
      return;
    case "TAKE_REST":
      playGameSound("fatigue.heal");
      return;
    case "REST_REMOVE":
      playGameSound("ui.confirm");
      return;
    case "CHOOSE_REWARD":
      playGameSound(command.cardId === "recover" ? "fatigue.heal" : "reward");
      return;
    case "CHOOSE_ORGANIZATION":
      playGameSound("reward");
      return;
    case "ACCEPT_EVENT":
      playGameSound(eventChoiceSound(previous));
      return;
    case "DECLINE_EVENT":
      playGameSound("ui.confirm");
      return;
    default:
      return;
  }
}
